/**
 * product-card.js  (v6 — Web Component)
 * =============================================================================
 * Defines <product-card> as a Custom Element so that every instance
 * self-initialises the moment it is connected to the DOM — even when
 * dynamically appended after page load (e.g. by the product-recommendations
 * section that fetches and injects HTML asynchronously).
 *
 * LOADING
 *   Load this file once globally (theme.liquid / global asset).
 *   No manual init calls needed; the browser handles everything via
 *   connectedCallback().
 *
 * DEPENDENCIES
 *   window.ctsProductData[PRODUCT_ID] must be set before or at the same
 *   time as the element is appended. The product-card.liquid snippet now
 *   injects this inline <script> block automatically, so the data is always
 *   present when connectedCallback fires.
 *
 *   Format:
 *     window.ctsProductData = window.ctsProductData || {};
 *     window.ctsProductData[PRODUCT_ID] = {
 *       options:  [...],   // product.options
 *       variants: [...]    // product.variants
 *     };
 *
 * PARAMETERS (via data-attributes, set by product-card.liquid):
 *   data-product-id           {String}  Shopify product ID.
 *   data-product-handle       {String}  Shopify product handle (for Ajax re-fetch).
 *   data-show-price-in-button {String}  "true" | "false"
 *                                       Controls where price is displayed.
 *   data-locked-opts          {String}  "POSITION:VALUE;" pairs for colour locking.
 *
 * TWO-PHASE RENDER
 *   Phase 1 (instant, Liquid data):
 *     Optimistic — enables the button for any matching variant regardless of
 *     `available`, because third-party inventory apps (e.g. Try Before You Buy)
 *     can set available:false on ALL variants.
 *
 *   Phase 2 (Ajax, ~100–300 ms):
 *     Fetches /products/{handle}.js for live inventory flags, then re-renders
 *     with accurate sold-out states.
 * =============================================================================
 */

(function () {
  'use strict';

  /* ── Money formatter ───────────────────────────────────────────────────── */
  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      var fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
      return window.Shopify.formatMoney(cents, fmt);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* ══════════════════════════════════════════════════════════════════════════
     ProductCard — Custom Element class
  ══════════════════════════════════════════════════════════════════════════ */
  class ProductCard extends HTMLElement {

    /* ── Lifecycle: fires every time element enters the DOM ─────────────── */
    connectedCallback() {
      /* Guard: skip if already initialised (e.g. element moved in DOM) */
      if (this._ctsInit) return;
      this._ctsInit = true;

      this._productId         = this.dataset.productId;
      this._productHandle     = this.dataset.productHandle;
      this._showPriceInButton = this.dataset.showPriceInButton !== 'false';
      this._variantsLive      = false;

      /* ── Read data from embedded <script type="application/json"> ───────
         Each <product-card> carries its own data — no shared global,
         no cross-section conflicts, works in AJAX-injected HTML.
      ─────────────────────────────────────────────────────────────────── */
      var data = this._readEmbeddedData();
      if (!data) {
        console.warn('[ProductCard] No variant data for product', this._productId);
        return;
      }
      this._variants = data.variants;

      /* ── Cache DOM refs ─────────────────────────────────────────────── */
      this._selects      = Array.from(this.querySelectorAll('.cts-select'));

      /* .cts-form is now a <div> (not <form>) to avoid nested-form conflicts
         when the card is rendered inside the cart drawer's <form>.
         The cart URL is stored on data-action instead. */
      this._formEl       = this.querySelector('.cts-form');
      this._variantInput = this._formEl && this._formEl.querySelector('.cts-variant-id');
      this._atcBtn       = this._formEl && this._formEl.querySelector('.cts-atc-btn');
      this._priceEl      = this._atcBtn && this._atcBtn.querySelector('.cts-atc-price');
      this._compareEl    = this._atcBtn && this._atcBtn.querySelector('.cts-atc-price-compare');
      this._labelEl      = this._atcBtn && this._atcBtn.querySelector('.cts-atc-label');

      /* Standalone price block (only present when show_price_in_button=false) */
      this._standalonePriceWrap = this.querySelector('.cts-item__price-standalone');
      this._standalonePriceCurr = this._standalonePriceWrap && this._standalonePriceWrap.querySelector('.cts-item__price-current');
      this._standalonePriceComp = this._standalonePriceWrap && this._standalonePriceWrap.querySelector('.cts-item__price-compare');

      /* ── Attach change listeners ────────────────────────────────────── */
      this._selects.forEach((sel) => {
        sel.addEventListener('change', () => this._updateUI());
      });

      /* ── Attach ATC listener on the BUTTON (not a form submit) ─────── */
      if (this._atcBtn) {
        this._atcBtn.addEventListener('click', () => this._handleAddToCart());
      }

      /* ── Phase 1: instant optimistic render ─────────────────────────── */
      this._ensureSelectValues();
      this._updateUI();

      /* ── Phase 2: fetch live data and re-render ─────────────────────── */
      this._fetchLiveVariants();
    }

    /* ══════════════════════════════════════════════════════════════════════
       Public API
    ══════════════════════════════════════════════════════════════════════ */
    getCardData() {
      var selectedVariantId = this._variantInput ? Number(this._variantInput.value) : null;
      var selectedVariant   = this._variants
        ? (this._variants.find((v) => v.id === selectedVariantId) || null)
        : null;
      return {
        productId:       this._productId,
        productHandle:   this._productHandle,
        lockedOpts:      this.dataset.lockedOpts || '',
        variantsLive:    this._variantsLive,
        selectedVariant: selectedVariant,
        allVariants:     this._variants || [],
      };
    }

    /* ══════════════════════════════════════════════════════════════════════
       Private helpers
    ══════════════════════════════════════════════════════════════════════ */

    /**
     * Read variant data embedded directly in this element.
     * 1st: <script type="application/json" class="cts-product-data"> — always
     *      works even in AJAX-injected HTML (browser never executes it).
     * 2nd: window.ctsProductData[productId] — legacy fallback.
     */
    _readEmbeddedData() {
      var scriptEl = this.querySelector('script.cts-product-data');
      if (scriptEl) {
        try { return JSON.parse(scriptEl.textContent); }
        catch (e) { console.warn('[ProductCard] JSON parse error for product', this._productId, e); }
      }
      if (window.ctsProductData && window.ctsProductData[this._productId]) {
        return window.ctsProductData[this._productId];
      }
      return null;
    }

    _ensureSelectValues() {
      this._selects.forEach((sel) => {
        var isSize = sel.dataset.isSize === 'true';
        if (!isSize && sel.value === '' && sel.options.length > 0) {
          sel.value = sel.options[0].value;
        }
      });
    }

    _getLockedValues() {
      var locked = {};
      var raw = this.dataset.lockedOpts;
      if (!raw) return locked;
      raw.split(';').forEach((pair) => {
        var idx = pair.indexOf(':');
        if (idx < 1) return;
        var pos = pair.slice(0, idx).trim();
        var val = pair.slice(idx + 1);
        if (pos && val !== '') locked[pos] = val;
      });
      return locked;
    }

    _getSelectValues() {
      var vals = this._getLockedValues();
      this._selects.forEach((sel) => {
        if (sel.value !== '') vals[sel.dataset.optionPosition] = sel.value;
      });
      return vals;
    }

    _variantMatches(v, vals) {
      return Object.keys(vals).every((pos) => v['option' + pos] === vals[pos]);
    }

    _isVariantAvailable(v) {
      if (v.available) return true;
      var mgmt = v.inventory_management;
      if (mgmt === null || mgmt === '' || mgmt === undefined) return true;
      if (v.inventory_policy === 'continue') return true;
      return false;
    }

    _isValueAvailable(targetSelect, testValue) {
      var testVals = this._getLockedValues();
      this._selects.forEach((sel) => {
        if (sel !== targetSelect && sel.value !== '') {
          testVals[sel.dataset.optionPosition] = sel.value;
        }
      });
      testVals[targetSelect.dataset.optionPosition] = testValue;
      return this._variants.some((v) => this._isVariantAvailable(v) && this._variantMatches(v, testVals));
    }

    _findVariant() {
      if (!this._selects.every((sel) => sel.value !== '')) return null;
      var vals = this._getSelectValues();
      return (
        this._variants.find((v) => this._isVariantAvailable(v) && this._variantMatches(v, vals)) ||
        this._variants.find((v) => this._variantMatches(v, vals)) ||
        null
      );
    }

    _updateAvailability() {
      this._selects.forEach((sel) => {
        var wrap = sel.closest('.cts-select-wrap');
        sel.querySelectorAll('option[data-original-value]').forEach((opt) => {
          var orig  = opt.dataset.originalValue;
          var avail = this._isValueAvailable(sel, orig);
          opt.textContent = avail ? orig : orig + ' \u2013 Sold Out';
          opt.classList.toggle('cts-opt-soldout', !avail);
        });
        if (wrap) {
          var currentOpt = sel.options[sel.selectedIndex];
          wrap.classList.toggle(
            'cts-select-wrap--soldout',
            !!(currentOpt && currentOpt.classList.contains('cts-opt-soldout'))
          );
        }
      });
    }

    _updateUI() {
      var vals = this._getSelectValues();
      var variant, available, soldOut;

      if (!this._variantsLive) {
        var allFilled = this._selects.every((sel) => sel.value !== '');
        variant   = allFilled
          ? (this._variants.find((v) => this._isVariantAvailable(v) && this._variantMatches(v, vals)) ||
             this._variants.find((v) => this._variantMatches(v, vals)))
          : null;
        available = !!variant;
        soldOut   = false;
      } else {
        this._updateAvailability();
        variant   = this._findVariant();
        available = !!(variant && this._isVariantAvailable(variant));
        soldOut   = !!(variant && !this._isVariantAvailable(variant));
      }

      /* ── Button state ─────────────────────────────────────────────── */
      if (this._atcBtn) {
        this._atcBtn.disabled = !available;
        this._atcBtn.setAttribute('aria-disabled', String(!available));
        this._atcBtn.classList.toggle('is-soldout', soldOut);
      }

      if (this._labelEl) {
        this._labelEl.textContent = soldOut ? 'SOLD OUT' : 'ADD TO BAG';
      }

      /* ── Price update ─────────────────────────────────────────────── */
      if (variant) {
        if (this._variantInput) this._variantInput.value = variant.id;

        var currentPrice = formatMoney(variant.price);
        var comparePrice = (variant.compare_at_price > variant.price)
                            ? formatMoney(variant.compare_at_price)
                            : null;

        if (this._showPriceInButton) {
          if (this._priceEl) this._priceEl.textContent = currentPrice;
          if (this._compareEl) {
            if (comparePrice) {
              this._compareEl.textContent   = comparePrice;
              this._compareEl.style.display = '';
            } else {
              this._compareEl.textContent   = '';
              this._compareEl.style.display = 'none';
            }
          }
        } else {
          if (this._standalonePriceCurr) this._standalonePriceCurr.textContent = currentPrice;
          if (this._standalonePriceComp) {
            if (comparePrice) {
              this._standalonePriceComp.textContent   = comparePrice;
              this._standalonePriceComp.style.display = '';
            } else {
              this._standalonePriceComp.textContent   = '';
              this._standalonePriceComp.style.display = 'none';
            }
          }
        }
      }
    }

    /* ── Fetch live variant data from Shopify ─────────────────────────── */
    _fetchLiveVariants(callback) {
      if (!this._productHandle) return;
      fetch('/products/' + this._productHandle + '.js')
        .then((r) => {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then((fresh) => {
          if (fresh && fresh.variants && fresh.variants.length) {
            this._variants     = fresh.variants;
            this._variantsLive = true;
            this._ensureSelectValues();
            this._updateUI();
          }
          if (typeof callback === 'function') callback();
        })
        .catch((err) => {
          console.warn('[ProductCard] Live variant fetch failed for "' + this._productHandle + '":', err);
          if (typeof callback === 'function') callback();
        });
    }

    /* ── AJAX add-to-cart ─────────────────────────────────────────────────
       Uses a direct JSON POST to /cart/add — no form submission needed.
       This is safe whether the card is inside a cart drawer <form> or not,
       because we never trigger a form submit event.
    ──────────────────────────────────────────────────────────────────────── */
    _handleAddToCart() {
      if (!this._atcBtn || this._atcBtn.disabled) return;

      var variantId = this._variantInput ? this._variantInput.value : null;
      if (!variantId) return;

      /* Cart add URL from data-action on the .cts-form div */
      var cartAddUrl = (this._formEl && this._formEl.dataset.action) || '/cart/add';

      if (this._labelEl) this._labelEl.textContent = 'ADDING\u2026';
      this._atcBtn.disabled = true;

      fetch(cartAddUrl, {
        method:  'POST',
        headers: {
          'Content-Type':     'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 })
      })
      .then((res) => {
        if (!res.ok) {
          var status = res.status;
          return res.json().catch(() => ({})).then((errBody) => {
            throw { status: status, message: (errBody && errBody.description) || null };
          });
        }
        return res.json().catch(() => null);
      })
      .then(() => {
        if (this._labelEl) this._labelEl.textContent = 'ADDED \u2713';

        ['cart:refresh', 'cart:updated', 'on:cart:add'].forEach((evt) => {
          document.dispatchEvent(new CustomEvent(evt, { bubbles: true }));
        });

        const trigger = document.querySelector('.js-mini-cart-trigger');
        if (trigger) trigger.click();

        setTimeout(() => {
          this._fetchLiveVariants(() => {
            if (this._labelEl && this._labelEl.textContent === 'ADDED \u2713') {
              this._labelEl.textContent = 'ADD TO BAG';
            }
            this._atcBtn.disabled = false;
          });
        }, 2200);
      })
      .catch((err) => {
        if (err && err.status === 422) {
          if (this._labelEl) this._labelEl.textContent = 'MAX QTY IN CART';
          setTimeout(() => {
            this._fetchLiveVariants(() => {
              if (this._labelEl && this._labelEl.textContent === 'MAX QTY IN CART') {
                this._labelEl.textContent = 'ADD TO BAG';
              }
              this._atcBtn.disabled = false;
            });
          }, 2500);
        } else {
          if (this._labelEl) this._labelEl.textContent = 'ADD TO BAG';
          this._atcBtn.disabled = false;
          this._fetchLiveVariants();
        }
      });
    }
  }

  /* ── Register the custom element ─────────────────────────────────────── */
  if (!customElements.get('product-card')) {
    customElements.define('product-card', ProductCard);
  }

  /* ══════════════════════════════════════════════════════════════════════════
     Public API — window.CtsProductCard
  ══════════════════════════════════════════════════════════════════════════ */
  window.CtsProductCard = {
    init: function () {
      console.info('[ProductCard] Manual init() not needed — Web Component auto-initialises.');
    },
    getAllCards: function () {
      return Array.from(document.querySelectorAll('product-card'))
        .map(function (card) {
          return typeof card.getCardData === 'function' ? card.getCardData() : null;
        })
        .filter(Boolean);
    },
    getCard: function (productId) {
      var el = document.querySelector('product-card[data-product-id="' + productId + '"]');
      return el && typeof el.getCardData === 'function' ? el.getCardData() : null;
    }
  };

})();



class RecommendationSlider extends HTMLElement {
    connectedCallback() {
    this.init();
    }

    init() {
    if (typeof Swiper === 'undefined') {
        setTimeout(() => this.init(), 100);
        return;
    }

    new Swiper(this.querySelector('.cart-recommdations-swiper'), {
        slidesPerView: 1.2,
        spaceBetween: 6,
        navigation: {
        nextEl: this.querySelector('.cart-recommdations-next'),
        prevEl: this.querySelector('.cart-recommdations-prev'),
        },
    });
    }
}

if (!customElements.get('recommendation-slider')) {
    customElements.define('recommendation-slider', RecommendationSlider);
}